import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Flex,
  LoadingOverlay,
  Box,
  PasswordInput,
  Select,
  ComboboxItem,
  SimpleGrid,
  Text,
  TextInput
} from '@mantine/core';
import { useStyles } from '@/containers/pages/user/components/sections/linked-emails/styles';
import { ILinkedEmail } from '@/entities/admin-app/email';
import { Modal } from '@/components/modal';
import { IconAt, IconChevronDown } from '@tabler/icons';
import {
  useLazyCreateEmailWithoutLinkedUserQuery,
  useGetEmailServerQuery,
  useLazyGetDomainsQuery
} from '@/entities/admin-app/email/api';
import { themeColors } from '@/theme/colors';
import { FormErrors, useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { getValidateErrors } from '@/lib/utils/validation';
import { z } from 'zod';
import { defaultMailServerPasswordOptions } from '@/mock-data/passwordOptions';
import { usePasswordSchema } from '@hooks/password/useGeneratePolicy';
import { universalNotification } from '@/lib/utils/notification';
import { EmailServerIntegrationType } from '@/services/api/emailServerApiArea/types';

interface ICreateEmailAccountProps {
  handleAddEmail?: (data: ILinkedEmail) => void;
  opened: boolean;
  handleClose: () => void;
  filialId: number;
}

export const CreateEmailAccount = ({
  opened,
  handleClose,
  handleAddEmail,
  filialId
}: ICreateEmailAccountProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [createEmail, { isFetching: createEmailFetching }] =
    useLazyCreateEmailWithoutLinkedUserQuery();
  const { data: emailServers, isLoading: isLoadingServers } = useGetEmailServerQuery({
    Id: filialId
  });
  const [getDomains, { data: serverDomains, isLoading: isLoadingDomains, reset: resetDomains }] =
    useLazyGetDomainsQuery();

  const NO_DOMAIN_VALUE = '__NO_DOMAIN__';
  const passwordRulesZ = usePasswordSchema(defaultMailServerPasswordOptions, t);

  const createAccountSchema = z.object({
    EmailServer: z.string().min(1, { message: t('validation.required') }),
    Login: z
      .string()
      .min(1, { message: t('validation.enterLogin') })
      .refine((data) => !/\s/.test(data), {
        message: t('validation.emptyLogin')
      }),
    Email: z
      .string()
      .min(1, { message: t('emailMinError') })
      .refine((data) => !/\s/.test(data), {
        message: t('validation.emptyEmail')
      }),
    Password: passwordRulesZ,
    Domain: z
      .string()
      .min(1, { message: t('domainMinError') })
      .refine((data) => data !== NO_DOMAIN_VALUE, {
        message: t('noDomainsForMailServer')
      })
  });

  const createAccountForm = useForm({
    initialValues: {
      EmailServer: '',
      Login: '',
      Email: '',
      Password: '',
      Domain: ''
    },
    validateInputOnChange: true,
    validate: (values: any): FormErrors => getValidateErrors(createAccountSchema, values)
  });

  const isDisabled =
    Object.keys(createAccountForm.errors).length !== 0 ||
    createEmailFetching ||
    !createAccountForm.isDirty() ||
    createAccountForm.values.Domain === NO_DOMAIN_VALUE;

  const emailServerList = useMemo(
    () =>
      Array.isArray(emailServers)
        ? emailServers?.map((server: any) => ({
            label: server.Name || `Server ${server.Id}`,
            value: String(server.Id)
          }))
        : [],
    [emailServers]
  );

  const domainList = useMemo(() => {
    if (!serverDomains) return [];

    if (Array.isArray(serverDomains) && serverDomains.length > 0) {
      return serverDomains.map((Item: any) => ({
        label: String(Item?.Domain),
        value: Item?.Domain
      })) as ComboboxItem[];
    }

    return [
      {
        label: t('noDomain'),
        value: NO_DOMAIN_VALUE,
        disabled: true
      }
    ] as ComboboxItem[];
  }, [serverDomains, t]);

  useEffect(() => {
    const selectedServerId = createAccountForm.values.EmailServer;

    if (selectedServerId && filialId) {
      resetDomains();
      createAccountForm.setFieldValue('Domain', '');
      getDomains({
        ServerId: Number(selectedServerId),
        CustomerId: filialId
      });
    }
  }, [createAccountForm.values.EmailServer, filialId]);

  useEffect(() => {
    if (serverDomains !== undefined) {
      if (serverDomains && serverDomains.length > 0) {
        const firstDomain = serverDomains[0]?.Domain;
        if (firstDomain) {
          createAccountForm.setFieldValue('Domain', firstDomain);
        }
      } else {
        createAccountForm.setFieldValue('Domain', NO_DOMAIN_VALUE);
      }
    }
  }, [serverDomains]);

  const modalClose = () => {
    createAccountForm.reset();
    handleClose();
  };

  const getCurrentError = (code: number) => {
    switch (code) {
      case 54001:
        return t('requestsErrors.notConfiguredCustomersEmail');
      case 54002:
        return t('requestsErrors.notSendEmail');
      case 54003:
        return t('requestsErrors.dontHaveEmail');
      case 54004:
        return t('requestsErrors.emailAlreadyActivated');
      case 54005:
        return t('requestsErrors.emailNotActivated');
      case 54006:
        return t('requestsErrors.inputEmptyEmail');
      case 54007:
        return t('requestsErrors.notExistsMxRecords');
      case 54008:
        return t('requestsErrors.cantCreateDomain');
      case 54009:
        return t('requestsErrors.notFoundEmailServer');
      case 54010:
        return t('requestsErrors.emailAlreadyLinked');
      case 54011:
        return t('requestsErrors.emailIsNotExists');
      case 54012:
        return t('requestsErrors.notFoundImapFolder');
      case 54013:
        return t('requestsErrors.cantCreateEmail');
      case 54014:
        return t('requestsErrors.cantLinkEmail');
      case 54015:
        return t('requestsErrors.emailExists');
      case 54016:
        return t('requestsErrors.invalidEmail');
      case 54017:
        return t('requestsErrors.emailServerError');
      case 54018:
        return t('requestsErrors.additionalEmailEqualsEmail');
      case 54019:
        return t('requestsErrors.invalidPassword');
      case 54020:
        return t('notifications.errors.integrationNotConfigured');
      case 54021:
        return t('requestsErrors.emailExists');

      default:
        return undefined;
    }
  };

  const handleCreate = async () => {
    createAccountForm.validate();
    if (!createAccountForm.isValid()) return;

    const { Email, Domain, Password, EmailServer, Login } = createAccountForm.values;

    try {
      const { data, error, isError, isSuccess } = await createEmail({
        Login,
        Password,
        Email: `${Email.toLowerCase()}@${Domain}`,
        EmailServer: {
          Id: Number(EmailServer)
        }
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const errorTitle = isError ? getCurrentError(error.data?.Code) : undefined;

      universalNotification({
        error,
        isError,
        isSuccess,
        errorTitle,
        successTitle: t('notifications.success.createdMailAccount')
      });

      if (error) {
        return;
      }

      if (handleAddEmail && data) {
        handleAddEmail({
          Id: data?.Id,
          Password,
          UserId: data?.UserId,
          Email: data?.Email,
          EmailServer: {
            Id: data?.EmailServer?.Id || 0,
            Name: data?.EmailServer?.Name,
            IntegrationType: EmailServerIntegrationType.None
          }
        });
      }

      if (isSuccess) {
        modalClose();
      }
    } catch {
      /* empty */
    }
  };

  const handleChange = (event: any) => {
    const { value } = event.currentTarget;
    if (!value.includes('@')) {
      createAccountForm.setFieldValue('Email', value);
    }
  };

  return (
    <Modal
      size="450px"
      opened={opened}
      onClose={modalClose}
      title={t('profile.createAccount')}
      zIndex={1300}
    >
      <Box className={classes.modalRoot}>
        {emailServerList?.length ? (
          <form className={classes.modalRoot}>
            <Flex gap="8px" direction="column" className={classes.modalContainer}>
              <Flex w="100%">
                <Select
                  comboboxProps={{ zIndex: 1300 }}
                  w="100%"
                  label={t('profile.emailServer') || 'Email Server'}
                  placeholder={t('profile.selectEmailServer') || 'Select email server'}
                  data={emailServerList}
                  withAsterisk
                  {...createAccountForm.getInputProps('EmailServer')}
                />
              </Flex>
              <Flex w="100%">
                <TextInput
                  w="100%"
                  label={t('profile.login') || 'Login'}
                  className={classes.textFieldInput}
                  placeholder={t('validation.enterLogin') || 'Enter login'}
                  withAsterisk
                  {...createAccountForm.getInputProps('Login')}
                />
              </Flex>
              <Flex w="100%" align="start">
                <TextInput
                  w="100%"
                  label={t('profile.email') || 'E-mail'}
                  className={classes.textFieldInput}
                  placeholder={t('profile.email')}
                  withAsterisk
                  {...createAccountForm.getInputProps('Email')}
                  onChange={handleChange}
                />
                <Flex mr="xs" ml="xs" mt="32px">
                  <IconAt size={22} color={themeColors.brandGray[0]} />
                </Flex>
                <Flex className={classes.createContainer}>
                  <Select
                    label={t('profile.domain') || 'Domain'}
                    data={domainList}
                    w="100%"
                    rightSectionWidth={domainList.length > 1 ? 25 : 0}
                    comboboxProps={{ zIndex: 1300 }}
                    rightSection={
                      domainList.length > 1 ? <IconChevronDown color="#98A2B3" size={18} /> : null
                    }
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    maxDropdownHeight={150}
                    allowDeselect={false}
                    data-cy="selectDomain"
                    className={domainList.length > 1 ? '' : classes.select}
                    classNames={{ dropdown: domainList.length > 1 ? '' : classes.dropdown }}
                    {...createAccountForm.getInputProps('Domain')}
                    value={createAccountForm.values.Domain}
                    onChange={(value) => createAccountForm.setFieldValue('Domain', value || '')}
                  />
                </Flex>
              </Flex>
              <SimpleGrid cols={1} w="100%">
                <PasswordInput
                  data-lpignore="true"
                  autoComplete="new-password"
                  className={classes.textFieldInput}
                  placeholder={t('profile.password')}
                  description={t('profile.passwordRequiring')}
                  label={t('profile.password')}
                  withAsterisk
                  {...createAccountForm.getInputProps('Password')}
                />
              </SimpleGrid>
            </Flex>
            <Flex justify="space-between" gap={12} p="16px 34px" w="100%">
              <Button
                size="sm"
                variant="filled"
                fullWidth
                disabled={isDisabled}
                onClick={handleCreate}
              >
                {t('create')}
              </Button>
              <Button fullWidth color="gray" variant="outline" onClick={modalClose}>
                {t('profile.cancel')}
              </Button>
            </Flex>
          </form>
        ) : (
          <Box className={classes.modalContainer}>
            <Text>{t('profile.createMailAccountRequiring')}</Text>
          </Box>
        )}
        <LoadingOverlay
          visible={isLoadingServers || isLoadingDomains || createEmailFetching}
          overlayProps={{ blur: 2 }}
        />
      </Box>
    </Modal>
  );
};
